export type InstallationAnalysisErrorStatus
  = | 'DUPLICATE_SERIAL_NUMBER'
    | 'WAITING_PARTNER_CONFIRMATION'
    | 'WAITING_GENERATION_CHECK'
    | 'GENERATION_CHECK_ERROR'
    | 'WAITING_CUSTOMER_CONFIRMATION'
    | 'REJECTED_BY_CUSTOMER'
    | 'APPROVED'

export interface InstallationAnalysisErrorConfig {
  stage: number
  iconSuffix: string
  customTitle: string
  hideAnalysisTag: boolean
  customTimestampText: string
  customHeaderTitle: string
  customHeaderDescription: string
}

const INSTALLATION_ANALYSIS_ERROR_CONFIGS: Record<InstallationAnalysisErrorStatus, InstallationAnalysisErrorConfig> = {
  DUPLICATE_SERIAL_NUMBER: {
    stage: 1,
    iconSuffix: '-error',
    customTitle: 'Número de série duplicado',
    hideAnalysisTag: true,
    customTimestampText: 'Erro em',
    customHeaderTitle: 'Número de série informado está em uso',
    customHeaderDescription: 'O inversor informado já foi ativado em outro projeto. Verifique se as informações enviadas estão corretas.',
  },
  WAITING_PARTNER_CONFIRMATION: {
    stage: 1,
    iconSuffix: '',
    customTitle: 'Aguardando confirmação do parceiro',
    hideAnalysisTag: false,
    customTimestampText: 'Última verificação em',
    customHeaderTitle: 'Aguardando confirmação do parceiro',
    customHeaderDescription: 'O parceiro está analisando os dados do inversor.',
  },
  WAITING_GENERATION_CHECK: {
    stage: 1,
    iconSuffix: '',
    customTitle: 'Verificando geração',
    hideAnalysisTag: false,
    customTimestampText: 'Última verificação em',
    customHeaderTitle: 'Verificando geração',
    customHeaderDescription: 'O sistema está verificando se o inversor está gerando energia.',
  },
  GENERATION_CHECK_ERROR: {
    stage: 1,
    iconSuffix: '-error',
    customTitle: 'Erro na verificação de geração',
    hideAnalysisTag: true,
    customTimestampText: 'Erro em',
    customHeaderTitle: 'Erro na verificação de geração',
    customHeaderDescription: 'Não foi possível verificar a geração do inversor. Verifique se está conectado corretamente.',
  },
  WAITING_CUSTOMER_CONFIRMATION: {
    stage: 2,
    iconSuffix: '',
    customTitle: 'Aguardando confirmação do cliente',
    hideAnalysisTag: false,
    customTimestampText: 'Última verificação em',
    customHeaderTitle: 'Aguardando confirmação do cliente',
    customHeaderDescription: 'Aguardando o cliente confirmar a instalação via WhatsApp.',
  },
  REJECTED_BY_CUSTOMER: {
    stage: 2,
    iconSuffix: '-error',
    customTitle: 'Cliente negou realização da instalação',
    hideAnalysisTag: true,
    customTimestampText: 'Validado em',
    customHeaderTitle: 'Instalação não reconhecida pelo cliente',
    customHeaderDescription: 'O cliente informou que a instalação ainda não foi realizada. Confirme com seu cliente e submeta a instalação para reanálise.',
  },
  APPROVED: {
    stage: 3,
    iconSuffix: '',
    customTitle: 'Instalação aprovada',
    hideAnalysisTag: false,
    customTimestampText: 'Aprovado em',
    customHeaderTitle: 'Instalação aprovada',
    customHeaderDescription: 'A instalação foi validada com sucesso!',
  },
} as const

const ERROR_STATUSES = Object.keys(INSTALLATION_ANALYSIS_ERROR_CONFIGS) as InstallationAnalysisErrorStatus[]

export default function useInstallationAnalysis() {
  function isStatusAnalysisError(status: string): status is InstallationAnalysisErrorStatus {
    return ERROR_STATUSES.includes(status as InstallationAnalysisErrorStatus)
  }

  function getErrorConfig(status: InstallationAnalysisErrorStatus): InstallationAnalysisErrorConfig | undefined {
    return INSTALLATION_ANALYSIS_ERROR_CONFIGS[status]
  }

  function isFatalError(status: InstallationAnalysisErrorStatus): boolean {
    const nonFatalErrors: InstallationAnalysisErrorStatus[] = [
      'REJECTED_BY_CUSTOMER',
      'APPROVED',
      'WAITING_PARTNER_CONFIRMATION',
      'WAITING_GENERATION_CHECK',
      'WAITING_CUSTOMER_CONFIRMATION',
    ]
    return !nonFatalErrors.includes(status)
  }

  return {
    // Validation
    isStatusAnalysisError,
    isFatalError,

    // Config getters
    getErrorConfig,
  }
}
